import { APIService, FetchOptions } from "./APIService";
import { LoggerService } from "../Logger/LoggerService";
import { ZodSchema } from "zod";
import { APIError, ValidationError } from "./Error";
import { QueryParamEntry, QueryParamRecord } from "./QueryBuilder";

const appendQueryParams = (url: URL, query: FetchOptions["query"]): void => {
  if (!query) {
    return;
  }

  if (query instanceof URLSearchParams) {
    query.forEach((value, key) => {
      url.searchParams.append(key, value);
    });
    return;
  }

  const entries = isQueryParamRecord(query)
    ? Object.entries(query)
    : (query as Iterable<QueryParamEntry>);

  for (const [key, value] of entries) {
    url.searchParams.append(key, value);
  }
};

const isQueryParamRecord = (
  query: FetchOptions["query"],
): query is QueryParamRecord => {
  return (
    Boolean(query) &&
    !Array.isArray(query) &&
    !(query instanceof URLSearchParams) &&
    typeof query === "object" &&
    !(Symbol.iterator in query)
  );
};

export class HttpAPIService implements APIService {
  #baseUrl: string;
  #logger: LoggerService;
  #getToken?: () => string | null;

  constructor(
    baseUrl: string,
    logger: LoggerService,
    getToken?: () => string | null,
  ) {
    this.#baseUrl = baseUrl;
    this.#logger = logger;
    this.#getToken = getToken;
  }

  async request<T>(options: FetchOptions, schema?: ZodSchema<T>): Promise<T> {
    const { path, method = "GET", query, body } = options;
    const url = this.#buildUrl(path);
    appendQueryParams(url, query);
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (this.#getToken) {
      const token = this.#getToken();
      if (token) headers["X-API-key"] = token;
    }
    const response = await fetch(url.toString(), {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new APIError(
        `HTTP error ${response.status}`,
        response.status,
        data,
      );
      this.#logger.captureException(error, {
        url: url.toString(),
        method,
        requestBody: body,
        responseData: data,
      });
      throw error;
    }
    if (!schema) {
      return data as T;
    }
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      const error = new ValidationError(
        "Invalid response format",
        parsed.error,
        data,
      );
      this.#logger.captureException(error, {
        url: url.toString(),
        method,
        requestBody: body,
        responseData: data,
      });
      throw error;
    }
    return parsed.data;
  }

  #buildUrl(path: string): URL {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;

    // Absolute API base (e.g. https://api.example.com/api/v1)
    if (/^https?:\/\//i.test(this.#baseUrl)) {
      const normalizedBase = this.#baseUrl.endsWith("/")
        ? this.#baseUrl
        : `${this.#baseUrl}/`;
      return new URL(normalizedPath.slice(1), normalizedBase);
    }

    // Relative API base (e.g. /api/v1) requires runtime origin.
    const origin = globalThis.location?.origin || "http://localhost";
    const normalizedBase = this.#baseUrl.startsWith("/")
      ? this.#baseUrl
      : `/${this.#baseUrl}`;

    return new URL(`${normalizedBase}${normalizedPath}`, origin);
  }
}
